import React, { useMemo, useState, useRef } from "react";
import InfoPage from "../InfoPage";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import { validateImageFile } from "../../utils/fileValidation";
import { showToast } from "../../components/Messaging/Toast";
import { uploadFile } from "../../utils/fileUpload";
import IntroVideoUpload from "../../components/IntroVideoUpload";
import ShowcaseUpload from "../../components/ShowcaseUpload";
import './HostOnboarding.sass';

const AMENITIES = [
  'hot_tub','swimming_pool','sauna','cold_plunge','firepit','outdoor_deck',
  'lake','mountains','beach','golf','skiing','pet_friendly','family_friendly',
  'free_parking','ev_charger','grill','smartlock','low_toxin','accessible','river_access',
  'pickleball','tennis_courts'
];

const PROPERTY_TYPES_VR = [
  'single_family_home','condo','townhome','tiny_home','cottage','guesthouse','cabin','a_frame'
];

const PROPERTY_TYPES_BH = [
  'boutique', 'resort', 'motel', 'micro_hotel', 'wellness', 'glamping', 'floating', 'igloo'
];

const TOURISM_FEATURES = [
  'lake', 'mountains', 'beach', 'golf', 'skiing_snowboarding', 'desert', 'rural', 'city',
  'state_park', 'national_park', 'sports', 'famous_landmarks', 'amusement_park'
];

const inputStyle = {
  border: 'none',
  borderBottom: '2px solid rgba(0,0,0,.12)',
  background: 'transparent',
  padding: '8px 0',
  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
  fontSize: '16px',
  color: '#0f084b',
  outline: 'none',
  width: '100%'
};

const labelStyle = {
  fontFamily: "'Sailors', serif",
  fontSize: '16px',
  color: '#0f084b',
  textAlign: 'left',
  marginTop: 6
};

const Chip = ({ text, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 10px', borderRadius: 16, background: '#f0f0f0',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12, color: '#0f084b'
  }}>
    {text}
    <button type="button" onClick={onRemove} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>✕</button>
  </span>
);

const HostOnboarding = () => {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const defaultEmail = params.get('email') || (typeof window !== 'undefined' ? window.localStorage.getItem('postaway_email') || '' : '');
  const hostCategory = (params.get('category') || 'vacation_rental').toLowerCase();
  const isAddingProperty = params.get('add') === 'new';
  const editPropertyId = params.get('edit');

  const [email] = useState(defaultEmail);

  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadSuccess, setImageUploadSuccess] = useState(false);
  const [introVideo, setIntroVideo] = useState(null);
  const [showcaseAssets, setShowcaseAssets] = useState([]);
  const fileInputRef = useRef(null);
  const [brandName, setBrandName] = useState("");
  const [handle, setHandle] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [type, setType] = useState(hostCategory === 'boutique_hotel' ? PROPERTY_TYPES_BH[0] : PROPERTY_TYPES_VR[0]);
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [sqft, setSqft] = useState("");
  const [sleeps, setSleeps] = useState("");
  const [nearestAirport, setNearestAirport] = useState("");
  const [airportMiles, setAirportMiles] = useState("");
  const [linktree, setLinktree] = useState("");
  const [directUrl, setDirectUrl] = useState("");
  const [airbnbUrl, setAirbnbUrl] = useState("");
  const [vrboUrl, setVrboUrl] = useState("");
  const [otherListingUrl, setOtherListingUrl] = useState("");
  const [expediaUrl, setExpediaUrl] = useState("");  // New field for Boutique Hotels
  
  // Tourism Board specific fields
  const [officialName, setOfficialName] = useState("");
  const [contactName, setContactName] = useState("");
  const [aboutLocation, setAboutLocation] = useState("");
  const [knownFor, setKnownFor] = useState("");
  const [population, setPopulation] = useState("");
  const [tourismFeatures, setTourismFeatures] = useState([]);
  const [acceptingClients, setAcceptingClients] = useState(null);  // true, false, or null
  
  const [igUrl, setIgUrl] = useState("");
  const [ttUrl, setTtUrl] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [ltkUrl, setLtkUrl] = useState("");
  const [amenities, setAmenities] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerInput, setPartnerInput] = useState("");
  const [collabText, setCollabText] = useState("");
  const [participatePurpose, setParticipatePurpose] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle state change - reset city when state changes
  const handleCountryChange = (value) => {
    setCountry(value);
    if (value !== country) {
      setState(""); // Reset state when country changes
      setCity(""); // Reset city when country changes
    }
  };

  const handleStateChange = (value) => {
    setState(value);
    if (value !== state) {
      setCity(""); // Reset city when state changes
    }
  };
  
  // Toggle tourism feature
  const toggleTourismFeature = (feature) => {
    setTourismFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const header = useMemo(() => (
    <>
      <h3 className="host-onboarding__header">{hostCategory === 'tourism_board' ? 'Tell us about your destination' : 'Enter your first property to get started. If you have more properties you can add those later.'}</h3>
      <div className="host-onboarding__subheader">This helps us match you with the right creators and campaigns.</div>
    </>
  ), [hostCategory]);

  const toggleAmenity = (key) => {
    setAmenities((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const addPartnerChip = () => {
    const v = partnerInput.trim();
    if (!v) return;
    setPartners((p) => [...p, v]);
    setPartnerInput("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Validate required fields BEFORE submitting
    if (hostCategory !== 'tourism_board') {
      // Property name is also required
      if (!name || !name.trim()) {
        setIsSubmitting(false);
        showToast('Please enter a Property Name before saving.', 'error', null, 4000);
        return;
      }
    } else {
      // For tourism boards, official name is required
      if (!officialName || !officialName.trim()) {
        setIsSubmitting(false);
        showToast('Please enter an Official Name before saving.', 'error', null, 4000);
        return;
      }
    }
    
    // If image exists but hasn't been uploaded yet, validate and upload it
    if (image && !imageUrl) {
      const validation = validateImageFile(image);
      if (!validation.isValid) {
        setIsSubmitting(false);
        showToast(validation.errorMessage, 'error', null, 4000);
        return;
      }
    }

    const firstShowcaseImage = Array.isArray(showcaseAssets)
      ? (showcaseAssets.find(a => a?.imageUrl || a?.image_url || a?.thumbnailUrl || a?.thumbnail_url)?.imageUrl ||
         showcaseAssets.find(a => a?.imageUrl || a?.image_url || a?.thumbnailUrl || a?.thumbnail_url)?.image_url ||
         showcaseAssets.find(a => a?.imageUrl || a?.image_url || a?.thumbnailUrl || a?.thumbnail_url)?.thumbnailUrl ||
         showcaseAssets.find(a => a?.imageUrl || a?.image_url || a?.thumbnailUrl || a?.thumbnail_url)?.thumbnail_url ||
         null)
      : null;

    // Without a photo the property never renders on the public /hosts page,
    // so make sure the host knows before they finish (skip when editing —
    // the property may already have an image that isn't loaded in this form).
    if (hostCategory !== 'tourism_board' && !editPropertyId && !image && !imageUrl && !firstShowcaseImage) {
      const proceed = window.confirm(
        "You haven't added a property photo yet. Without one, your property won't appear on the Postaway hosts page.\n\nPress Cancel to add a photo now, or OK to continue without one (you can add it later from your profile)."
      );
      if (!proceed) {
        setIsSubmitting(false);
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      let imagePublicUrl = imageUrl || firstShowcaseImage;
      if (!imagePublicUrl && image) imagePublicUrl = await uploadFile(image);

      const socials = {
        instagram: igUrl ? { url: igUrl } : null,
        tiktok: ttUrl ? { url: ttUrl } : null,
        youtube: ytUrl ? { url: ytUrl } : null,
        ltk: ltkUrl ? { url: ltkUrl } : null,
      };

      const listings = {
        direct_booking_url: directUrl || null,
        airbnb_url: airbnbUrl || null,
        vrbo_url: vrboUrl || null,
        other_listing_url: otherListingUrl || null,
      };

      const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4000' : '';
      
      const requestBody = {
        email,
        hostCategory,  // Include the host category
        brandName: brandName?.trim() || null,
        handle: handle?.trim() || null,
        locationCountry: country?.trim() || null,
        locationCity: city?.trim() || null,
        locationState: state?.trim() || null,
        linktree: linktree?.trim() || null,
        partners,
        description: collabText?.trim() || null,
        participatePurpose: participatePurpose || false,
        introVideo,
        showcaseAssets,
      };

      // Add property data for vacation rental and boutique hotel
      if (hostCategory !== 'tourism_board') {
        requestBody.property = {
          name: name?.trim() || null,
          location_country: country?.trim() || null,
          location_city: city?.trim() || null,
          location_state: state?.trim() || null,
          type,
          bedrooms: bedrooms ? Number(bedrooms) : null,
          bathrooms: bathrooms ? Number(bathrooms) : null,
          square_footage: sqft ? Number(sqft) : null,
          sleeps: sleeps ? Number(sleeps) : null,
          nearest_airport: nearestAirport?.trim() || null,
          driving_distance_miles: airportMiles ? Number(airportMiles) : null,
          direct_booking_url: directUrl?.trim() || null,
          airbnb_url: airbnbUrl?.trim() || null,
          vrbo_url: vrboUrl?.trim() || null,
          other_listing_url: otherListingUrl?.trim() || null,
          expedia_url: hostCategory === 'boutique_hotel' ? (expediaUrl?.trim() || null) : null,
          listings,
          amenities,
          socials,
          image_url: imagePublicUrl || null,
          collab_opportunity: collabText?.trim() || null,
        };
      } else {
        // Add tourism board specific data
        requestBody.tourism = {
          official_name: officialName?.trim() || null,
          contact_name: contactName?.trim() || null,
          location_country: country?.trim() || null,
          location_city: city?.trim() || null,
          location_state: state?.trim() || null,
          logo_url: imagePublicUrl || null,
          linktree_url: linktree?.trim() || null,
          socials,
          about: aboutLocation?.trim() || null,
          known_for: knownFor?.trim() || null,
          population: population ? Number(population) : null,
          features: tourismFeatures,
          accepting_clients: acceptingClients,
        };
      }
      
      // If adding a new property after initial onboarding
      if (isAddingProperty) {
        requestBody.operation = 'add_property';
      }
      // If editing existing property
      if (editPropertyId) {
        requestBody.operation = 'update_property';
        requestBody.property_id = editPropertyId;
      }
      
      const res = await fetch(`${apiBase}/api/host/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      // Parse the response to get error details
      const responseData = await res.json();
      
      if (!res.ok || !responseData.ok) {
        // Handle specific error cases
        let errorMessage = 'There was an error saving your profile. Please try again.';
        
        if (responseData.error === 'db_error') {
          // Check if it's a constraint violation error
          if (responseData.message?.includes('name') || responseData.message?.includes('null value')) {
            errorMessage = 'Please make sure you have filled in the Brand Name and Property Name fields.';
          } else {
            errorMessage = 'A database error occurred. Please check all required fields are filled and try again.';
          }
        } else if (responseData.error === 'missing_email') {
          errorMessage = 'Please provide a valid email address.';
        } else if (responseData.error) {
          errorMessage = `Error: ${responseData.error}. Please check your input and try again.`;
        }
        
        setIsSubmitting(false);
        showToast(errorMessage, 'error', null, 6000);
        return;
      }
      
      // Success - save email to localStorage
      try { 
        if (email) {
          localStorage.setItem('postaway_email', email);
          localStorage.setItem('postaway_user_type', 'host');
        }
      } catch (_) {}
      
      // Show success message before redirect
      showToast('Profile saved successfully!', 'success', null, 2000);
      
      const qs = new URLSearchParams({ email: email || '' }).toString();
      
      // Small delay to show success message before redirect
      setTimeout(() => {
        // Navigate appropriately based on context
        if (isAddingProperty || editPropertyId) {
          window.location.assign(`/collab-board?${qs}`);
        } else {
          window.location.assign(`/host/checkout?${qs}`);
        }
      }, 500);
      
    } catch (err) {
      setIsSubmitting(false);
      // eslint-disable-next-line no-console
      console.error('Save error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'There was an error saving your profile. Please try again.';
      
      if (err.message === 'save_failed') {
        errorMessage = 'Failed to save profile. Please check your connection and try again.';
      } else if (err.message === 'upload_failed') {
        errorMessage = 'Failed to upload image. Please try again with a different image.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}. Please try again.`;
      }
      
      showToast(errorMessage, 'error', null, 6000);
    }
  };

  // Helper function to trigger file input with cross-browser support
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      // Use showPicker() if available (Chrome 99+), fallback to click()
      if ('showPicker' in HTMLInputElement.prototype) {
        try {
          fileInputRef.current.showPicker();
        } catch (err) {
          // If showPicker fails, fall back to click
          fileInputRef.current.click();
        }
      } else {
        // Fallback for older browsers
        fileInputRef.current.click();
      }
    }
  };

  const belowContent = (
    <>
      {/* Left - Property Image */}
      <div className="host-onboarding__left">
        <label 
          htmlFor="propimg" 
          onClick={(e) => {
            e.preventDefault();
            triggerFileInput();
          }}
          className="host-onboarding__image-label"
          style={{ marginBottom: 24 }}
        >
          {imageUploading ? (
            <>
              <span className="host-onboarding__image-uploading">
                Uploading and converting image...
              </span>
              <small style={{ color: '#666' }}>This may take up to 30 seconds for HEIC files</small>
            </>
          ) : imageUploadSuccess ? (
            <>
              <span className="host-onboarding__image-success-icon">✓</span>
              <span className="host-onboarding__image-success-text">
                Image uploaded successfully!
              </span>
            </>
          ) : imageUrl ? (
            <img alt="property" src={(typeof window !== 'undefined' && window.location.hostname === 'localhost' && imageUrl.startsWith('/')) ? `http://localhost:4000${imageUrl}` : imageUrl} className="host-onboarding__image-img" />
          ) : image ? (
            <img alt="property" src={URL.createObjectURL(image)} className="host-onboarding__image-img" />
          ) : (
            <div className="host-onboarding__image-text">
              Upload Main Property Photo (JPEG, PNG, or iPhone photo). Required to appear on the Postaway hosts page.
            </div>
          )}
        </label>
        <input 
          ref={fileInputRef}
          id="propimg" 
          type="file" 
          accept="image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif,.heic,.heif" 
          style={{ display: 'none' }} 
          onChange={async (e) => {
          const file = e.target.files?.[0] || null;
          if (file) {
            // Validate file type
            const validation = validateImageFile(file);
            if (!validation.isValid) {
              showToast(validation.errorMessage, 'error', null, 4000);
              e.target.value = ''; // Clear the input
              setImage(null);
              setImageUrl(null);
              return;
            }
            
            setImage(file);
            setImageUploading(true);
            setImageUploadSuccess(false);
            
            try {
              // Upload file directly (handles HEIC conversion)
              const uploadedUrl = await uploadFile(file);
              setImageUrl(uploadedUrl);
              setImage(null); // Clear file after successful upload
              setImageUploadSuccess(true);
              
              // Hide checkmark after 2 seconds
              setTimeout(() => setImageUploadSuccess(false), 2000);
            } catch (error) {
              console.error('Upload failed:', error);
              showToast('Failed to upload image. Please try again.', 'error', null, 4000);
              setImage(null);
              setImageUrl(null);
            } finally {
              setImageUploading(false);
            }
          } else {
            setImage(null);
            setImageUrl(null);
          }
        }} 
        />

        <IntroVideoUpload
          className="host-onboarding__intro-video"
          value={introVideo}
          onChange={setIntroVideo}
        />
        <ShowcaseUpload
          className="host-onboarding__showcase"
          mode="onboarding"
          value={showcaseAssets}
          onChange={setShowcaseAssets}
        />
        <p className="host-onboarding__welcome-description">
          Skip for now and add one later from your dashboard.
        </p>

        <div className="host-onboarding__welcome-text">
          <p className="host-onboarding__welcome-title">
            Welcome to the Postaway Host Community
          </p>
          <p className="host-onboarding__welcome-description">
            Connect with talented content creators and showcase your {hostCategory === 'tourism_board' ? 'unique destination' : 'unique property'} to a wider audience.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="host-onboarding__right">
        <form onSubmit={handleSave} className="host-onboarding__form">
          {header}

          {/* Tourism Board: Official Name and Contact Name, or Regular: Brand Name */}
          {hostCategory === 'tourism_board' ? (
            <>
              <div>
                <input 
                  className="host-onboarding__input"
                  value={officialName} 
                  onChange={(e) => setOfficialName(e.target.value)} 
                  placeholder="" 
                  required
                />
                <label className="host-onboarding__label">
                  Official Name (Required){' '}
                  <span className="host-onboarding__label-required">*</span>
                </label>
              </div>
              <div>
                <input className="host-onboarding__input" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="" />
                <label className="host-onboarding__label">Contact Name</label>
              </div>
              <div>
                <div className="host-onboarding__input-wrapper">
                  <span className="host-onboarding__input-prefix">@</span>
                  <input 
                    className="host-onboarding__input host-onboarding__input-with-prefix"
                    value={handle} 
                    onChange={(e) => {
                      // Remove @ sign if user types it
                      const value = e.target.value.replace('@', '');
                      setHandle(value);
                    }} 
                    placeholder="yourhandle" 
                  />
                </div>
                <label className="host-onboarding__label">Handle (Display Name)</label>
              </div>
            </>
          ) : (
            <>
              <div>
                <input 
                  className="host-onboarding__input"
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  placeholder="" 
                  required
                />
                <label className="host-onboarding__label">
                  Property Name (Required){' '}
                  <span className="host-onboarding__label-required">*</span>
                </label>
              </div>
              <div>
                <div className="host-onboarding__input-wrapper">
                  <span className="host-onboarding__input-prefix">@</span>
                  <input 
                    className="host-onboarding__input host-onboarding__input-with-prefix"
                    value={handle} 
                    onChange={(e) => {
                      // Remove @ sign if user types it
                      const value = e.target.value.replace('@', '');
                      setHandle(value);
                    }} 
                    placeholder="yourhandle" 
                  />
                </div>
                <label className="host-onboarding__label">Handle (Display Name)</label>
              </div>
              <div>
                <input 
                  className="host-onboarding__input"
                  value={brandName} 
                  onChange={(e) => setBrandName(e.target.value)} 
                  placeholder=""
                />
                <label className="host-onboarding__label">
                  Brand Name
                </label>
              </div>
            </>
          )}
          <div className="host-onboarding__row">
            <div className="host-onboarding__field">
              <LocationAutocomplete
                type="country"
                value={country}
                onChange={handleCountryChange}
                placeholder="Start typing country..."
                label="Country"
              />
            </div>
          </div>

          <div className="host-onboarding__row">
            <div className="host-onboarding__field">
              <LocationAutocomplete
                type="state"
                value={state}
                onChange={handleStateChange}
                placeholder={country === "United States" ? "Start typing state..." : "State (optional)"}
                label="State"
                disabled={country && country !== "United States"}
              />
            </div>
            <div className="host-onboarding__field">
              <LocationAutocomplete
                type="city"
                value={city}
                onChange={setCity}
                stateValue={state}
                placeholder={state ? "Start typing city..." : country === "United States" ? "Select state first" : "Start typing city..."}
                label="City"
                disabled={country === "United States" && !state}
              />
            </div>
          </div>
          {/* Property Type Selection */}
          {hostCategory !== 'tourism_board' && (
          <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
              {(hostCategory === 'boutique_hotel' ? PROPERTY_TYPES_BH : PROPERTY_TYPES_VR).map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setType(t)}
                  aria-pressed={type === t}
                  style={{
                    borderRadius: 18,
                    padding: '6px 12px',
                    border: '1px solid rgba(0,0,0,.2)',
                    cursor: 'pointer',
                    background: type === t ? '#0f084b' : 'transparent',
                    color: type === t ? '#fff' : '#0f084b',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: 12
                  }}
                >
                  {t.replace(/_/g,' ')}
                </button>
              ))}
            </div>
            <label style={labelStyle}>Property Type</label>
          </div>
          )}
          {hostCategory === 'vacation_rental' && (
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <input className="host-onboarding__input" type="number" inputMode="numeric" value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} placeholder="" />
              <label style={labelStyle}># Bedrooms</label>
            </div>
            <div style={{ flex: 1 }}>
              <input className="host-onboarding__input" type="number" inputMode="decimal" step="0.5" value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} placeholder="" />
              <label style={labelStyle}># Bathrooms</label>
            </div>
          </div>
          )}
          {hostCategory === 'vacation_rental' && (
          <div>
            <input className="host-onboarding__input" type="number" inputMode="numeric" value={sqft} onChange={(e) => setSqft(e.target.value)} placeholder="" />
            <label style={labelStyle}>Square Footage</label>
          </div>
          )}
          {hostCategory === 'vacation_rental' && (
          <div>
            <input className="host-onboarding__input" type="number" inputMode="numeric" value={sleeps} onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 5);
              setSleeps(v);
            }} placeholder="" />
            <label style={labelStyle}>Property Sleeps</label>
          </div>
          )}
          {hostCategory !== 'tourism_board' && (
          <div>
            <input className="host-onboarding__input" value={nearestAirport} onChange={(e) => setNearestAirport(e.target.value)} placeholder="" />
            <label style={labelStyle}>Nearest Airport</label>
          </div>
          )}
          {hostCategory !== 'tourism_board' && (
          <div>
            <input className="host-onboarding__input" type="number" inputMode="numeric" value={airportMiles} onChange={(e) => setAirportMiles(e.target.value)} placeholder="" />
            <label style={labelStyle}>Driving distance (miles) from nearest airport</label>
          </div>
          )}
          <div>
            <input className="host-onboarding__input" value={linktree} onChange={(e) => setLinktree(e.target.value)} placeholder="" />
            <label style={labelStyle}>Website</label>
          </div>

          {/* Listings */}
          {hostCategory !== 'tourism_board' && (
          <div>
            <input className="host-onboarding__input" value={directUrl} onChange={(e) => setDirectUrl(e.target.value)} placeholder="" />
            <label style={labelStyle}>Direct Booking Link</label>
          </div>
          )}
          {hostCategory !== 'tourism_board' && (
          <div>
            <input className="host-onboarding__input" value={airbnbUrl} onChange={(e) => setAirbnbUrl(e.target.value)} placeholder="" />
            <label style={labelStyle}>Airbnb Link</label>
          </div>
          )}
          {hostCategory !== 'tourism_board' && (
          <div>
            <input className="host-onboarding__input" value={vrboUrl} onChange={(e) => setVrboUrl(e.target.value)} placeholder="" />
            <label style={labelStyle}>Vrbo Link</label>
          </div>
          )}
          {hostCategory === 'boutique_hotel' && (
          <div>
            <input className="host-onboarding__input" value={expediaUrl} onChange={(e) => setExpediaUrl(e.target.value)} placeholder="" />
            <label style={labelStyle}>Expedia Link</label>
          </div>
          )}
          {hostCategory !== 'tourism_board' && (
          <div>
            <input className="host-onboarding__input" value={otherListingUrl} onChange={(e) => setOtherListingUrl(e.target.value)} placeholder="" />
            <label style={labelStyle}>{hostCategory === 'boutique_hotel' ? 'Other Listing Link' : 'Expedia or Other Listing Link'}</label>
          </div>
          )}

          {/* Socials (URLs only) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <input className="host-onboarding__input" value={igUrl} onChange={(e) => setIgUrl(e.target.value)} />
              <label style={labelStyle}>Instagram URL</label>
            </div>
            <div>
              <input className="host-onboarding__input" value={ttUrl} onChange={(e) => setTtUrl(e.target.value)} />
              <label style={labelStyle}>TikTok URL</label>
            </div>
            <div>
              <input className="host-onboarding__input" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} />
              <label style={labelStyle}>YouTube URL</label>
            </div>
            {hostCategory !== 'tourism_board' && hostCategory !== 'boutique_hotel' && (
            <div>
              <input className="host-onboarding__input" value={ltkUrl} onChange={(e) => setLtkUrl(e.target.value)} />
              <label style={labelStyle}>LTK URL</label>
            </div>
            )}
          </div>

          {/* Amenities for Properties / Features for Tourism Boards */}
          {hostCategory !== 'tourism_board' ? (
            <div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
                {AMENITIES.map((a) => (
                  <button type="button" key={a} onClick={() => toggleAmenity(a)}
                    style={{
                      borderRadius: 18, padding: '6px 12px', border: '1px solid rgba(0,0,0,.2)', cursor: 'pointer',
                      background: amenities.includes(a) ? '#0f084b' : 'transparent', color: amenities.includes(a) ? '#fff' : '#0f084b',
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12
                    }}>{a.replace(/_/g,' ')}</button>
                ))}
              </div>
              <label style={labelStyle}>Property Amenities</label>
            </div>
          ) : (
            <>
              {/* Tourism Board Specific Fields */}
              <div>
                <textarea 
                  style={{ 
                    ...inputStyle, 
                    minHeight: 80, 
                    resize: 'vertical',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
                  }} 
                  value={aboutLocation} 
                  onChange={(e) => setAboutLocation(e.target.value)}
                  placeholder=""
                />
                <label className="host-onboarding__label">About Your Location</label>
              </div>
              <div>
                <input className="host-onboarding__input" value={knownFor} onChange={(e) => setKnownFor(e.target.value)} placeholder="" />
                <label className="host-onboarding__label">What Are You Known For?</label>
              </div>
              <div>
                <input className="host-onboarding__input" type="number" inputMode="numeric" value={population} onChange={(e) => setPopulation(e.target.value)} placeholder="" />
                <label className="host-onboarding__label">Population</label>
              </div>
              <div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
                  {TOURISM_FEATURES.map((f) => (
                    <button type="button" key={f} onClick={() => toggleTourismFeature(f)}
                      style={{
                        borderRadius: 18, padding: '6px 12px', border: '1px solid rgba(0,0,0,.2)', cursor: 'pointer',
                        background: tourismFeatures.includes(f) ? '#0f084b' : 'transparent', color: tourismFeatures.includes(f) ? '#fff' : '#0f084b',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12
                      }}>{f.replace(/_/g,' ')}</button>
                  ))}
                </div>
                <label className="host-onboarding__label">What applies to your area? (Check all that apply)</label>
              </div>
              <div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setAcceptingClients(true)}
                    style={{
                      flex: 1,
                      borderRadius: 6,
                      padding: '10px',
                      border: acceptingClients === true ? '2px solid #0f084b' : '1px solid rgba(0,0,0,.2)',
                      background: acceptingClients === true ? '#0f084b' : 'transparent',
                      color: acceptingClients === true ? '#fff' : '#0f084b',
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      cursor: 'pointer'
                    }}
                  >
                    Currently Accepting Collaborations
                  </button>
                  <button
                    type="button"
                    onClick={() => setAcceptingClients(false)}
                    style={{
                      flex: 1,
                      borderRadius: 6,
                      padding: '10px',
                      border: acceptingClients === false ? '2px solid #0f084b' : '1px solid rgba(0,0,0,.2)',
                      background: acceptingClients === false ? '#0f084b' : 'transparent',
                      color: acceptingClients === false ? '#fff' : '#0f084b',
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      cursor: 'pointer'
                    }}
                  >
                    Not Currently Accepting Collaborations
                  </button>
                </div>
                <label className="host-onboarding__label">Collaboration Status</label>
              </div>
            </>
          )}

          {/* Partners */}
          <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6, marginBottom: 8 }}>
              {partners.map((p, i) => (
                <Chip key={`${p}-${i}`} text={p} onRemove={() => setPartners((arr) => arr.filter((_, idx) => idx !== i))} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} value={partnerInput} onChange={(e) => setPartnerInput(e.target.value)} placeholder="Add partner" />
              <button type="button" onClick={addPartnerChip} style={{
                background: '#0f084b', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14
              }}>Add</button>
            </div>
            <label style={labelStyle}>{hostCategory === 'boutique_hotel' ? 'Past Collaborations' : 'Collaboration Partners'}</label>
          </div>
          
          {/* Postaway with a Purpose */}
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <input 
                type="checkbox"
                id="participate-purpose"
                checked={participatePurpose}
                onChange={(e) => setParticipatePurpose(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  marginTop: '2px',
                  cursor: 'pointer',
                  accentColor: '#dc1d08'
                }}
              />
              <div style={{ flex: 1 }}>
                <label 
                  htmlFor="participate-purpose"
                  style={{
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: '16px',
                    color: '#0f084b',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  Participate in Postaway with a Purpose
                  <div 
                    style={{ 
                      position: 'relative', 
                      display: 'inline-block',
                      cursor: 'help'
                    }}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ color: '#777E90' }}
                    >
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 16v-4"/>
                      <path d="M12 8h.01"/>
                    </svg>
                    {showTooltip && (
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginBottom: '8px',
                        padding: '12px 16px',
                        backgroundColor: '#141416',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                        width: '320px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        lineHeight: '1.5',
                        textAlign: 'left'
                      }}>
                        <div style={{
                          position: 'absolute',
                          bottom: '-6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 0,
                          height: 0,
                          borderLeft: '6px solid transparent',
                          borderRight: '6px solid transparent',
                          borderTop: '6px solid #141416'
                        }}/>
                        At Postaway, we believe that everyone deserves the opportunity to travel. 
                        Many lack the time, resources, and access to enjoy the getaways they deserve, however. 
                        If you are willing to offer a complimentary 2-night stay in your property one time 
                        in the next calendar year to host a frontline hero, please click yes. 
                        No arrangements will be made without your consent.
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting} 
            className="host-onboarding__submit"
          >
            {isSubmitting ? 'Saving...' : (isAddingProperty ? (hostCategory === 'tourism_board' ? 'Add Profile' : 'Add Property') : editPropertyId ? (hostCategory === 'tourism_board' ? 'Update Profile' : 'Update Property') : (hostCategory === 'tourism_board' ? 'Save Profile' : 'Save Property'))}
          </button>
        </form>
      </div>
    </>
  );

  const pageTitle = isAddingProperty ? "Add New Property" : editPropertyId ? "Edit Property" : "Host Onboarding";
  return <InfoPage title={pageTitle} divider belowContent={belowContent} disableStamps tallOverride={false} customMinHeight={2250} />;
};

export default HostOnboarding;


